import { useState } from 'react';
import { Loader2, Globe, Lock, Sparkles, X } from 'lucide-react';
import { INTENSITIES, RADII, fieldCost, buildSeconds, PUBLIC_DISCOUNT } from '../lib/config.js';
import { distanceMeters } from '../lib/geo.js';
import { Button, Sheet } from './ui.jsx';

const HOURS_LABEL = { 2: '2 hours', 24: '1 day', 168: '7 days', 720: '30 days' };

/**
 * Creation sheet, sized to fit a phone screen without scrolling: three
 * single-row choices, one summary line, one button.
 *
 * The cost shown here uses the same formula the server enforces
 * (src/lib/config.js mirrors field_cost() in SQL), so the price cannot surprise
 * the user — and cannot be tampered with either, since the server recomputes it.
 */
/**
 * Fields do not cross. The server refuses a new field that overlaps a live
 * field its creator can see: public over public, or private over your own
 * private. Private over public, public over private, and private over a
 * stranger's private are fine. The list here is what the map shows, which is
 * exactly what can block — so this mirrors the server's answer before paying.
 */
function crossing({ fields, position, radius, isPublic }) {
  if (!position) return null;
  return fields.find((f) =>
    (f.status === 'active' || f.status === 'building')
    && ((isPublic && f.is_public) || (!isPublic && !f.is_public && f.is_mine))
    && distanceMeters(position, { lat: f.latitude, lng: f.longitude }) <= f.radius_m + radius) ?? null;
}
const KIND_NAME = (k) => INTENSITIES[k]?.name ?? k;

export default function FieldModal({ tokens, onCancel, onCreate, onBuyTokens, fields = [], position = null }) {
  const [intensity, setIntensity] = useState('glow');
  const [radius, setRadius] = useState(30);
  const [isPublic, setIsPublic] = useState(false);   // private by default
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const cost = fieldCost({ intensity, radiusMeters: radius, isPublic });
  const fullCost = fieldCost({ intensity, radiusMeters: radius, isPublic: false });
  const affordable = tokens >= cost;
  const buildS = buildSeconds(radius);
  const blocker = crossing({ fields, position, radius, isPublic });
  const buildLabel = buildS < 120 ? `${buildS}s` : `${Math.round(buildS / 60)} min`;

  const submit = async () => {
    setBusy(true); setError(null);
    try { await onCreate({ intensity, radiusMeters: radius, isPublic }); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <Sheet onClose={onCancel}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Consecrate the ground</p>
          <h3 className="display text-[1.7rem] mt-1">Energize <span className="display-i lit">this area</span>.</h3>
        </div>
        <button onClick={onCancel} aria-label="Close" className="btn btn-glass !p-2 !rounded-full shrink-0"><X size={15} /></button>
      </div>

      <Row label="Radius">
        <div className="grid grid-cols-3 gap-2">
          {RADII.map((r) => {
            const on = radius === r.meters;
            return (
              <button key={r.meters} type="button" onClick={() => setRadius(r.meters)}
                      className="chip py-2.5 px-2 text-center" data-on={on}>
                <span className="block display text-lg leading-none">{r.meters}m</span>
                <span className="block text-[0.7rem] mt-1 nums" style={{ color: on ? 'var(--gold)' : 'var(--text-3)' }}>
                  {fieldCost({ intensity, radiusMeters: r.meters, isPublic })} tokens
                </span>
              </button>
            );
          })}
        </div>
      </Row>

      <Row label="Duration">
        <div className="grid grid-cols-4 gap-2">
          {Object.values(INTENSITIES).map((i) => {
            const on = intensity === i.key;
            return (
              <button key={i.key} type="button" onClick={() => setIntensity(i.key)}
                      className="chip py-2.5 px-1.5 text-center" data-on={on}>
                <span className="block text-[0.8rem] font-semibold leading-none">{i.name}</span>
                <span className="block text-[0.7rem] mt-1" style={{ color: on ? 'var(--gold)' : 'var(--text-3)' }}>
                  {HOURS_LABEL[i.hours]}
                </span>
              </button>
            );
          })}
        </div>
      </Row>

      <Row label="Visibility">
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setIsPublic(false)} className="chip py-2.5 px-3" data-on={!isPublic}>
            <span className="flex items-center gap-2 text-[0.8rem] font-semibold"><Lock size={13} /> Private</span>
            <span className="block text-[0.7rem] mt-0.5" style={{ color: 'var(--text-3)' }}>Only you can connect</span>
          </button>
          <button type="button" onClick={() => setIsPublic(true)} className="chip py-2.5 px-3" data-on={isPublic}>
            <span className="flex items-center gap-2 text-[0.8rem] font-semibold"><Globe size={13} /> Public</span>
            <span className="block text-[0.7rem] mt-0.5" style={{ color: 'var(--sage)' }}>Shared · −{PUBLIC_DISCOUNT * 100}%</span>
          </button>
        </div>
      </Row>

      {/* one-line summary */}
      <div className="glass-flat px-4 py-3 flex items-center justify-between gap-3 text-sm nums">
        <span className="inline-flex items-baseline gap-1.5">
          {isPublic && <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>{fullCost}</span>}
          <span className="display text-2xl leading-none" style={{ color: 'var(--gold)' }}>{cost}</span>
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>tokens</span>
        </span>
        <span className="text-xs" style={{ color: 'var(--text-2)' }}>builds in {buildLabel}</span>
        <span className="text-xs" style={{ color: affordable ? 'var(--text-2)' : 'var(--rust)' }}>
          balance {tokens}
        </span>
      </div>

      {blocker && !error && (
        <p className="text-sm px-3.5 py-2.5 rounded-xl leading-relaxed"
           style={{ color: 'var(--rust)', background: 'color-mix(in oklab, var(--rust) 12%, transparent)' }}>
          Fields do not cross. This one would overlap {blocker.is_mine ? 'your' : 'a public'} {KIND_NAME(blocker.intensity)} field
          {' '}{Math.round(distanceMeters(position, { lat: blocker.latitude, lng: blocker.longitude }))}m away —
          {isPublic ? ' make it private, or move along a little.' : ' choose a smaller radius, or move along a little.'}
        </p>
      )}
      {error && (
        <p className="text-sm px-3.5 py-2.5 rounded-xl"
           style={{ color: 'var(--rust)', background: 'color-mix(in oklab, var(--rust) 12%, transparent)' }}>
          {error}
        </p>
      )}

      {blocker ? (
        <Button variant="mint" size="lg" className="w-full !py-3.5" disabled>Fields cannot cross</Button>
      ) : affordable ? (
        <Button variant="mint" size="lg" className="w-full !py-3.5" disabled={busy} onClick={submit}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><Sparkles size={15} /> Energize · {cost} tokens</>}
        </Button>
      ) : (
        <Button variant="gold" size="lg" className="w-full !py-3.5" onClick={onBuyTokens}>
          Not enough tokens · get more
        </Button>
      )}
    </Sheet>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-2">
      <p className="eyebrow">{label}</p>
      {children}
    </div>
  );
}
